import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Zoom from '@mui/material/Zoom'
import Typography from '@mui/material/Typography'
import PhonePrefixDropdown from '../dropdowns/PhonePrefixDropdown'
import themeColors from '@/assets/theme/colors'
import { TooltipPlacement } from '@/types/global_types'
import { getFontBody, getFontBodySmall } from '@/assets/theme/fonts'

type Props = {
  icon?: 'person' | 'email' | 'check' | 'lock'
  validation?: null | number
  title?: string | React.ReactElement
  tooltip?: string | React.ReactNode
  tooltipPlacement?: TooltipPlacement
  width?: number | string
  prefixWidth?: number | string
  height?: number
  marginBottom?: number | string
  marginTop?: number | string
  iconColor?: string
  color?: string
  placeholder: string
  disabled?: boolean
  inputMarginLeft?: number | string
  value: string // stored as "prefix:number" e.g. "+41:791234567"
  display?: 'horizontal' | 'vertical'
  onEnterKeyDown?: () => void
  onClick?: () => void
  onBlur?: () => void
  onChange: (prefix: string, value: string) => void
}

export default function PhoneInput(props: Props) {
  const [focused, setFocused] = useState(false)
  const [prefix, setPrefix] = useState('')
  const [number, setNumber] = useState('')

  // Split the combined value if the user autofills
  useEffect(() => {
    if (props.value.includes(':')) {
      const [pfx, val] = props.value.split(':')
      setPrefix(pfx)
      setNumber(val)
    } else {
      setNumber(props.value)
    }
  }, [props.value])

  const getBorderColor = () => {
    if (props.validation === 1) return themeColors.success.darker
    if (props.validation === -1) return themeColors.error.main
    if (focused) return '#5a6dcd'
    return '#ddd'
  }

  // When the number input changes
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value

    // Detect prefix if number starts with '+'
    if (val.startsWith('+')) {
      // Simple heuristic: get first 2-4 chars as prefix
      const detectedPrefix = val.slice(0, 3) // or implement your country code map
      const restNumber = val.slice(detectedPrefix.length)
      console.log(detectedPrefix, restNumber)
      setPrefix(detectedPrefix)
      setNumber(restNumber)
      props.onChange(detectedPrefix, restNumber)
    } else {
      setNumber(val)
      props.onChange(prefix, val)
    }
  }

  // When the prefix dropdown changes
  const handlePrefixChange = (newPrefix: string) => {
    setPrefix(newPrefix)
    setNumber('') // reset number if new prefix is selected
    props.onChange(newPrefix, '')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && props.onEnterKeyDown) props.onEnterKeyDown()
  }

  return (
    <Box
      sx={{
        display: props.display === 'horizontal' ? 'flex' : 'block',
        alignItems: 'center',
        marginBottom: props.marginBottom ?? 0,
        marginTop: props.marginTop ?? 0,
        width: props.width ?? '100%',
        gap: props.display === 'horizontal' ? 1 : 0
      }}
    >
      {props.title && (
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: getFontBodySmall(),
            marginBottom: 0
          }}
        >
          {props.title}
        </Typography>
      )}

      <Box
        sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}
      >
        {/* Prefix Dropdown */}
        <Box
          sx={{
            width: props.prefixWidth ?? 180,
            display: 'flex',
            alignItems: 'center',
            height: props.height ?? 40
          }}
        >
          <PhonePrefixDropdown
            selectedOption={prefix}
            disabled={props.disabled}
            onChange={handlePrefixChange}
            height={props.height}
            fontSize={14}
            valueContainerMarginTop={0}
          />
        </Box>

        {/* Phone Number Input */}
        <Tooltip
          title={props.tooltip ?? ''}
          arrow
          placement={props.tooltipPlacement ?? 'right'}
          TransitionComponent={Zoom}
          enterDelay={300}
        >
          <TextField
            fullWidth
            size="small"
            placeholder={props.placeholder}
            value={number}
            onChange={handleNumberChange}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false)
              props.onBlur?.()
            }}
            onClick={props.onClick}
            onKeyDown={handleKeyDown}
            disabled={props.disabled}
            InputProps={{
              autoComplete: 'tel',
              sx: {
                height: props.height ?? 40,
                fontSize: getFontBody(),
                display: 'flex',
                alignItems: 'center'
              }
            }}
            sx={{
              background: '#fff',
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: getBorderColor() },
                '&:hover fieldset': { borderColor: getBorderColor() },
                '&.Mui-focused fieldset': { borderColor: getBorderColor() }
              }
            }}
          />
        </Tooltip>
      </Box>
    </Box>
  )
}
